import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { credentialsApi, type CredentialDetail } from '@/api/credentials';
import { type CredentialTypeSchemaResponse } from '@/api/credentialSchemas';
import { tenantsApi } from '@/api/tenants';
import { base64ToBytes, decryptCredential, encryptCredentialForShares, type CredentialPayload } from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';
import { formatDateTime } from '@/lib/format';
import { confirmDialog } from '@/stores/dialog';
import { inferFallbackSecretAttrs } from '@/features/vault/detail/shared';
import { FileBlobDownload } from '@/features/vault/detail/FileBlobDownload';

// ==========================================================
// VersionsSection — istoric arhivat, reveal per versiune
// ==========================================================

interface VersionsSectionProps {
  cred: CredentialDetail;
  keyPair: { publicKey: Uint8Array; privateKey: Uint8Array };
  schema: CredentialTypeSchemaResponse | undefined;
}

export function VersionsSection({ cred, keyPair, schema }: VersionsSectionProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['credential-versions', cred.id],
    queryFn: () => credentialsApi.listVersions(cred.id),
    enabled: expanded,
  });

  const invalidateAfterRestore = () => {
    queryClient.invalidateQueries({ queryKey: ['credential', cred.id] });
    queryClient.invalidateQueries({ queryKey: ['credential-versions', cred.id] });
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'credentials',
    });
  };

  return (
    <section className="pt-4 border-t border-[var(--border)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 text-xs uppercase text-[var(--text-muted)] font-semibold hover:text-[var(--text)] dark:hover:text-[var(--text)]"
      >
        <span>{expanded ? '▼' : '▶'}</span>
        {t('vault_ui.detail.versions_title')}
        {query.data && (
          <span className="text-[10px] normal-case text-[var(--text-dim)]">
            ({query.data.length})
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2">
          {query.isLoading && <p className="text-xs text-[var(--text-muted)]">{t('common.loading')}</p>}
          {query.isError && (
            <p className="text-xs text-red-600">
              {t('vault_ui.detail.error_with_message', { message: String(query.error) })}
            </p>
          )}
          {query.data && query.data.length === 0 && (
            <p className="text-xs text-[var(--text-muted)]">{t('vault_ui.detail.no_versions')}</p>
          )}
          {query.data?.map((v) => (
            <VersionCard
              key={v.id}
              cred={cred}
              version={v.version}
              changedAt={v.changed_at}
              changeReason={v.change_reason}
              changeSource={v.change_source}
              keyPair={keyPair}
              schema={schema}
              onRestored={invalidateAfterRestore}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface VersionCardProps {
  cred: CredentialDetail;
  version: number;
  changedAt: string;
  changeReason: string | null;
  changeSource: string | null;
  keyPair: { publicKey: Uint8Array; privateKey: Uint8Array };
  schema: CredentialTypeSchemaResponse | undefined;
  onRestored: () => void;
}

function VersionCard({
  cred,
  version,
  changedAt,
  changeReason,
  changeSource,
  keyPair,
  schema,
  onRestored,
}: VersionCardProps) {
  const { t } = useTranslation();
  const [plain, setPlain] = useState<CredentialPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const user = useAuthStore((s) => s.user);
  const currentTenantId = useAuthStore((s) => s.currentTenantId);

  const reveal = async () => {
    setError(null);
    setBusy(true);
    try {
      const detail = await credentialsApi.getVersion(cred.id, version);
      const decoded = await decryptCredential(
        {
          ciphertext: detail.ciphertext,
          nonce: detail.nonce,
          dek_wrapped: detail.dek_wrapped,
        },
        keyPair,
      );
      setPlain(decoded);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vault_ui.detail.decrypt_error'));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!await confirmDialog(t('vault_ui.detail.restore_confirm', { version }))) {
      return;
    }
    setError(null);
    setRestoring(true);
    try {
      // Decrypt plaintext-ul vechi (dacă nu e deja decriptat din reveal).
      let oldPlain = plain;
      if (!oldPlain) {
        const detail = await credentialsApi.getVersion(cred.id, version);
        oldPlain = (await decryptCredential(
          {
            ciphertext: detail.ciphertext,
            nonce: detail.nonce,
            dek_wrapped: detail.dek_wrapped,
          },
          keyPair,
        )) as CredentialPayload;
      }

      // Re-encrypt cu DEK nou + re-wrap pt TOȚI shares (forward secrecy).
      if (!currentTenantId) {
        throw new Error(t('vault_ui.detail.no_space_selected'));
      }
      const [shares, members] = await Promise.all([
        credentialsApi.listShares(cred.id),
        tenantsApi.listMembers(currentTenantId),
      ]);
      const recipients = shares.map((s) => {
        const m = members.find((mm) => mm.user_id === s.user_id);
        if (!m) {
          throw new Error(
            t('vault_ui.detail.share_user_without_member', { userId: s.user_id }),
          );
        }
        return {
          userId: s.user_id,
          publicKey: base64ToBytes(m.public_key),
          isSelf: s.user_id === user?.id,
        };
      });
      const encrypted = await encryptCredentialForShares(oldPlain, recipients);

      // PATCH cu meta curent (păstrăm) + plaintext vechi + title curent.
      // Meta-ul vechi nu e disponibil la restore — credential_versions nu-l
      // arhivează. User poate edita meta separat dacă vrea.
      await credentialsApi.update(cred.id, {
        title: cred.title,
        meta: cred.meta,
        ciphertext: encrypted.ciphertext,
        nonce: encrypted.nonce,
        dek_wrapped: encrypted.dek_wrapped,
        dek_wraps: encrypted.dek_wraps,
        change_reason: `restored from v${version}`,
      });
      onRestored();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vault_ui.detail.restore_error'));
    } finally {
      setRestoring(false);
    }
  };

  const secretAttrs = schema?.attributes.filter((a) => a.secret) ?? [];

  return (
    <div className="border border-[var(--border)] rounded-md px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[var(--text)]">v{version}</span>
            <span className="text-xs text-[var(--text-muted)]">
              {formatDateTime(changedAt)}
            </span>
            {changeSource && (
              <span className="badge bg-[var(--bg-surface)] text-[var(--text-muted)] text-[10px]">
                {changeSource}
              </span>
            )}
          </div>
          {changeReason && (
            <div className="text-xs text-[var(--text-muted)] mt-0.5 italic truncate">
              „{changeReason}"
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="text-xs text-[var(--text-muted)] hover:text-brand-700 dark:hover:text-brand-300 disabled:opacity-50"
            onClick={restore}
            disabled={busy || restoring}
            title={t('vault_ui.detail.restore_title', { version })}
          >
            {restoring ? t('vault_ui.detail.restoring') : t('vault_ui.detail.restore')}
          </button>
          {plain ? (
            <button
              type="button"
              className="text-xs text-[var(--text-muted)] hover:text-brand-700 dark:hover:text-brand-300"
              onClick={() => setPlain(null)}
            >
              {t('vault.hide')}
            </button>
          ) : (
            <button
              type="button"
              className="text-xs text-brand-700 dark:text-brand-300 hover:underline disabled:opacity-50"
              onClick={reveal}
              disabled={busy || restoring}
            >
              {busy ? t('vault_ui.detail.decrypting') : t('vault.reveal')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</div>
      )}

      {plain && (
        <dl className="mt-3 space-y-2 pt-3 border-t border-[var(--border)]">
          {(secretAttrs.length > 0 ? secretAttrs : inferFallbackSecretAttrs(plain)).map(
            (attr) => {
              const raw = plain[attr.key];
              if (raw === undefined || raw === null || raw === '') return null;
              const value = String(raw);
              return (
                <div key={attr.key}>
                  <dt className="text-[10px] uppercase text-[var(--text-muted)] mb-1">
                    {attr.label}
                  </dt>
                  {attr.type === 'file' ? (
                    <FileBlobDownload
                      base64={value}
                      filename={
                        typeof cred.meta?.['filename'] === 'string'
                          ? (cred.meta['filename'] as string)
                          : 'secure-file'
                      }
                    />
                  ) : attr.type === 'textarea' ? (
                    <dd className="whitespace-pre-wrap bg-[var(--bg-surface)] border border-[var(--border)] rounded p-2 text-xs font-mono text-[var(--text)]">
                      {value}
                    </dd>
                  ) : (
                    <dd className="font-mono text-xs break-all bg-[var(--bg-surface)] border border-[var(--border-strong)] rounded px-2 py-1.5 text-[var(--text)]">
                      {value}
                    </dd>
                  )}
                </div>
              );
            },
          )}
        </dl>
      )}
    </div>
  );
}
